import { useState, useEffect, useCallback, useRef } from 'react';
import _ from 'lodash';
import Sidebar from './App/Sidebar';
import SelectResources from './App/SelectResources';
import FooterLinks from './App/FooterLinks';
import Game from './App/Game';
import { calculateGhostDemands, initialGhostDemands } from './calculateGhostDemands';

export const initialPicks = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
};

export const initialResults = {
    1: ['Y', 'Y', 'Y', 'Y', 'Y'],
    2: ['', '', '', '', ''],
    3: ['', '', '', '', ''],
    4: ['', '', '', '', ''],
    5: ['', '', '', '', '']
};

export const initialProb = {
    2: null,
    3: null,
    4: null,
    5: null
};

const resourceOrder = ['coins', 'supplies', 'marble', 'steel', 'planks', 'crystal', 'scrolls', 'silk', 'elixir', 'magicDust', 'gem', 'orc', 'mana', 'divineSeeds', 'moonstone', 'platinum', 'elvenTreeGum', 'obsidian', 'arcaneInk', 'velvet', 'sillySoap', 'alloyShrooms', 'cosmicBismuth'];

function sortBasedOnArray(parentArray) {
  return [...parentArray].sort((a, b) => {
    let indexA = resourceOrder.indexOf(a);
    let indexB = resourceOrder.indexOf(b);
    if (indexA === -1) indexA = Infinity;
    if (indexB === -1) indexB = Infinity;
    return indexA - indexB;
  });
}

const getContextValues = (initialObject, oldObject, key, value) => {
  const clonedObject = { ...oldObject };
  const keys = Object.keys(clonedObject);
  for (let objectKey of keys) {
    if (Number(objectKey) === key) {
      clonedObject[objectKey] = value;
    } else if (objectKey > key) {
      clonedObject[objectKey] = initialObject[objectKey];
    }
  }
  return clonedObject;
};

function App() {
  const [view, setView] = useState('resources');
  const [choice, setChoice] = useState(0);
  const [selectedResources, setSelectedResources] = useState(['coins', 'supplies']);
  const [brain, setBrain] = useState({});
  const [picks, setPicks] = useState(initialPicks);
  const [results, setResults] = useState(initialResults);
  const [prob, setProb] = useState(initialProb);
  const [jokerData, setJokerData] = useState(null);

  // Refs to hold current state for the exposed API (avoids stale closures)
  const stateRef = useRef({ view, choice, selectedResources, brain, picks, results, prob, jokerData });
  useEffect(() => {
    stateRef.current = { view, choice, selectedResources, brain, picks, results, prob, jokerData };
  }, [view, choice, selectedResources, brain, picks, results, prob, jokerData]);

  // Sync the ref before the React setter so window.SpireWizard reads are immediate
  const updateJokerData = useCallback((data) => {
    stateRef.current = { ...stateRef.current, jokerData: data };
    setJokerData(data);
  }, []);

  const toggleResource = useCallback((resource) => {
    setSelectedResources((prev) => {
      let newResources;
      if (_.includes(prev, resource)) {
        newResources = sortBasedOnArray(_.without(prev, resource));
      } else {
        newResources = sortBasedOnArray([...prev, resource]);
      }
      stateRef.current = { ...stateRef.current, selectedResources: newResources };
      return newResources;
    });
  }, []);

  const setResourceResult = useCallback((choiceNum, index, color) => {
    console.log('[setResult] called with:', { choiceNum, index, color });
    console.log('[setResult] stateRef.current.results BEFORE:', JSON.stringify(stateRef.current.results));

    // Compute new results from ref (not from React state) for immediate sync
    const currentResults = stateRef.current.results;
    const newResults = { ...currentResults };
    newResults[choiceNum] = [...newResults[choiceNum]];
    newResults[choiceNum][index] = color;

    console.log('[setResult] newResults computed:', JSON.stringify(newResults));

    // Sync ref FIRST, before calling React setters
    stateRef.current = { ...stateRef.current, choice: choiceNum, results: newResults };
    console.log('[setResult] stateRef.current.results AFTER sync:', JSON.stringify(stateRef.current.results));

    // Then update React state
    setChoice(choiceNum);
    setResults(newResults);
  }, []);

  const startGame = useCallback(() => {
    setSelectedResources((currentResources) => {
      if (currentResources.length < 3 || currentResources.length > 8) return currentResources;

      let firstResources = [0, 1, 2, 3, 4];
      if (currentResources.length === 3) {
        firstResources = [0, 1, 2, 0, 1];
      } else if (currentResources.length === 4) {
        firstResources = [0, 1, 2, 3, 0];
      }

      const newPicks = { 1: firstResources, 2: [], 3: [], 4: [], 5: [] };
      const newProb = { 2: null, 3: null, 4: null, 5: null };
      const newResults = { 1: ['Y', 'Y', 'Y', 'Y', 'Y'], 2: ['', '', '', '', ''], 3: ['', '', '', '', ''], 4: ['', '', '', '', ''], 5: ['', '', '', '', ''] };

      setView('game');
      setChoice(1);
      setPicks(newPicks);
      setProb(newProb);
      setResults(newResults);

      stateRef.current = { ...stateRef.current, view: 'game', choice: 1, picks: newPicks, prob: newProb, results: newResults };
      updateJokerData(initialGhostDemands(currentResources.length));

      return currentResources;
    });
  }, [updateJokerData]);

  const goHome = useCallback(() => {
    setView('resources');
    setSelectedResources(['coins', 'supplies']);
    setChoice(0);
    stateRef.current = { ...stateRef.current, view: 'resources', selectedResources: ['coins', 'supplies'], choice: 0 };
  }, []);

  const handleUndo = useCallback(() => {
    setChoice((prevChoice) => {
      if (prevChoice <= 1) return prevChoice;
      const newChoice = prevChoice - 1;

      setPicks((prev) => {
        const updated = { ...prev };
        updated[prevChoice] = [];
        return updated;
      });
      setResults((prev) => {
        const updated = { ...prev };
        updated[prevChoice] = ['', '', '', '', ''];
        // Reset the previous round's results back to all yellow so user can re-enter
        updated[newChoice] = ['Y', 'Y', 'Y', 'Y', 'Y'];
        return updated;
      });
      setProb((prev) => {
        const updated = { ...prev };
        if (updated[prevChoice] !== undefined) updated[prevChoice] = null;
        return updated;
      });

      // Reset joker data to initial possibilities since we're going back
      const currentResources = stateRef.current.selectedResources;
      updateJokerData(initialGhostDemands(currentResources.length));

      return newChoice;
    });
  }, [updateJokerData]);

  useEffect(() => {
    window.SpireWizard = {
      // Resource selection
      toggleResource,
      selectResources: (resourceNames) => {
        const valid = resourceNames.filter(r => resourceOrder.includes(r));
        const sorted = sortBasedOnArray(valid);
        setSelectedResources(sorted);
        stateRef.current = { ...stateRef.current, selectedResources: sorted };
      },
      getSelectedResources: () => stateRef.current.selectedResources,

      // Game control
      startGame,
      goHome,
      undo: handleUndo,

      // Color/result setting
      setResult: setResourceResult,

      // Submit a choice
      submitChoice: (choiceNum) => {
        console.log('[submitChoice] called with choiceNum:', choiceNum);
        console.log('[submitChoice] stateRef.current.results:', JSON.stringify(stateRef.current.results));
        console.log('[submitChoice] stateRef.current.picks:', JSON.stringify(stateRef.current.picks));

        // Read current state fresh each time
        const { selectedResources, brain, picks, results } = stateRef.current;
        console.log('[submitChoice] destructured results:', JSON.stringify(results));

        const resourceCount = selectedResources.length;
        const nextChoice = choiceNum + 1;

        if (resourceCount === 3 && nextChoice > 3) return;
        if (resourceCount === 4 && nextChoice > 4) return;
        if (resourceCount > 5 && nextChoice > 5) return;

        const getQuery = (c, p, r) => {
          let query = '';
          if (c >= 2) query += p[1].join('') + '/' + r[1].join('');
          if (c >= 3) query += '/' + p[2].join('') + '/' + r[2].join('');
          if (c >= 4) query += '/' + p[3].join('') + '/' + r[3].join('');
          if (c >= 5) query += '/' + p[4].join('') + '/' + r[4].join('');
          return query;
        };

        const data = _.get(brain, `${resourceCount}.${nextChoice}`);
        const query = getQuery(nextChoice, picks, results);

        const applyCombo = (nextCombo) => {
          if (!nextCombo) {
            alert('No combination found for this encounter. Please check your color selection.');
            return;
          }
          // Re-read current state to avoid stale closures
          const { selectedResources: currentResources, picks: currentPicks, results: currentResults, prob: currentProb } = stateRef.current;
          const splitCombo = nextCombo.next_combo.split('');
          const nextPicks = splitCombo.map((num) => num === '-' ? '-' : parseInt(num));

          const newPicks = getContextValues(initialPicks, currentPicks, nextChoice, nextPicks);
          const newResults = getContextValues(initialResults, currentResults, nextChoice, nextPicks.map((pick) => pick === '-' ? 'G' : 'Y'));
          const newProb = getContextValues(initialProb, currentProb, nextChoice, nextCombo.prob);

          setPicks(newPicks);
          setResults(newResults);
          setProb(newProb);
          setChoice(nextChoice);

          stateRef.current = { ...stateRef.current, picks: newPicks, results: newResults, prob: newProb, choice: nextChoice };
          const jokerResult = calculateGhostDemands(currentResources, currentPicks, currentResults);
          updateJokerData(jokerResult);
        };

        if (!data) {
          fetch(`${process.env.PUBLIC_URL}/json/R${resourceCount}-C${nextChoice}.json`)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((tempData) => {
              const newBrain = { ...stateRef.current.brain, [`${resourceCount}.${nextChoice}`]: tempData };
              setBrain(newBrain);
              stateRef.current = { ...stateRef.current, brain: newBrain };
              applyCombo(_.get(tempData, query));
            })
            .catch((error) => console.error("Failed to load JSON:", error));
        } else {
          applyCombo(_.get(data, query));
        }
      },

      // Joker suggestion / ghost deduction
      getJokerSuggestion: () => {
        const { jokerData: current, selectedResources: currentResources, choice: currentChoice } = stateRef.current;
        if (!current) return null;

        const names = (set) => [...set].map((idx) => currentResources[idx]);

        return {
          recommended: current.jokerRecommended,
          // 0-based index into the ghost slots; the UI label shows ghostNumber
          ghostIndex: current.jokerGhostIndex,
          ghostNumber: current.jokerGhostIndex === null ? null : current.jokerGhostIndex + 1,
          // The banner is only rendered for 6+ resource games
          displayed: current.jokerRecommended && currentResources.length >= 6,
          choice: currentChoice,
          // How many demand assignments still fit the colours entered so far.
          // 1 means the board is fully solved (the brain reports 100% here);
          // 0 means the colours are impossible and nothing could be deduced.
          assignmentCount: current.assignmentCount ?? null,
          certain: current.certain ?? false,
          contradiction: current.contradiction ?? false,
          ghosts: current.possibilities.map((set, ghost) => ({
            ghostIndex: ghost,
            ghostNumber: ghost + 1,
            // An empty set is a ghost that already went green and is out of play.
            satisfied: set.size === 0,
            determined: set.size === 1,
            count: set.size,
            resourceIndexes: [...set],
            resources: names(set),
          })),
        };
      },

      // Read full state
      getState: () => stateRef.current,
    };

    return () => { delete window.SpireWizard; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative flex flex-col items-center h-screen w-full overflow-x-hidden overflow-y-auto pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))] pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
      {/* my-auto centres when the content fits and collapses to 0 when it overflows,
          so tall content (short landscape screens, 5-round games) scrolls from the top
          instead of being clipped like justify-center would. */}
      <div className="my-auto flex flex-col items-center">
        <div className="hidden md:block text-center mb-4">
            <h1 className="text-2xl font-bold text-white tracking-tight">Spire Wizard</h1>
            <p className="text-sm text-primary-muted">Diplomacy made simple</p>
        </div>
        <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl shadow-card p-4 md:p-8 group">
            { view === 'resources' &&
                <SelectResources
                    resources={selectedResources}
                    setResources={setSelectedResources}/> }
            { view === 'game' &&
                <Game
                    resources={selectedResources}
                    choice={choice}
                    setChoice={setChoice}
                    brain={brain}
                    setBrain={setBrain}
                    picks={picks}
                    setPicks={setPicks}
                    prob={prob}
                    setProb={setProb}
                    results={results}
                    setResults={setResults}
                    jokerData={jokerData}
                    setJokerData={updateJokerData}/>
            }
            <Sidebar resources={selectedResources} setResources={setSelectedResources} setView={setView} setChoice={setChoice} setPicks={setPicks} setProb={setProb} setResults={setResults} choice={choice} setJokerData={updateJokerData} onUndo={handleUndo} />
        </div>
        { view === 'resources' && <FooterLinks /> }
      </div>
    </div>
  );
}

export default App;
